import { api } from '@/api/client'
import { projectItemHref, projectTabHref } from '@/features/projects/projectLinks'
import type { SearchResult, SearchResultKind } from '@/types/search'

export const searchKeys = {
  all: ['search'] as const,
  query: (term: string, projectId?: string | null) =>
    [...searchKeys.all, term, projectId ?? null] as const,
}

export const searchApi = {
  search: (query: string, options: { projectId?: string | null; limitPerKind?: number } = {}) =>
    api.get<SearchResult>('/api/search', {
      query: {
        query,
        projectId: options.projectId,
        limitPerKind: options.limitPerKind ?? 5,
      },
    }),
}

/** Kortere søgeord matcher for meget til at være til nytte. Samme grænse som i API'et. */
export const MINIMUM_QUERY_LENGTH = 2

export const SEARCH_KIND_LABELS: Record<SearchResultKind, string> = {
  Project: 'Projekter',
  Document: 'Dokumenter',
  Drawing: 'Tegninger',
  Task: 'Opgaver',
  Defect: 'Mangler',
  Tender: 'Udbud',
  Material: 'Materialer',
  Facility: 'Bygninger',
  Asset: 'Anlæg',
}

/**
 * Hvor et resultat fører hen.
 *
 * Stien lægges af `projectItemHref`, så søgningen og beskederne bygger den ens.
 * De byggede den før hver for sig og begge med et `?tab=`, som projektsiden
 * ikke læser — den læser `fane`. Hvert eneste træf landede på overblikket.
 *
 * Findes der ikke en dialog for typen, lander man på fanen. Det er stadig ét
 * klik fra målet og bedre end en død URL.
 */
export function searchHitHref(kind: SearchResultKind, id: string, projectId: string | null): string {
  if (kind === 'Project') {
    return `/app/projects/${id}`
  }

  // Alt andet end et projekt ligger inde i et. Serveren sender altid sagen med,
  // men typen tillader null, og en streng med "undefined" i er værre end en
  // liste, man selv kan lede videre i.
  if (!projectId) {
    return '/app/projects'
  }

  switch (kind) {
    case 'Document':
      return projectItemHref(projectId, 'documents', 'documentId', id)
    case 'Drawing':
      return projectItemHref(projectId, 'drawings', 'drawingId', id)
    case 'Task':
      return projectItemHref(projectId, 'tasks', 'taskId', id)
    case 'Defect':
      return projectItemHref(projectId, 'quality', 'defectId', id)
    case 'Tender':
      return projectItemHref(projectId, 'tenders', 'tenderId', id)
    case 'Material':
      return projectTabHref(projectId, 'sustainability')
    case 'Facility':
    case 'Asset':
      return projectTabHref(projectId, 'operations')
  }
}
